import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { useDatasetSessionStorageContext } from '../../../hooks/context/useDatasetSessionStorageContext';
import { getDataKey } from '../../../utils/getDataKey';
import useRoutes from '../../../hooks/useRoutes';
import { isFromPageReload } from '../../../utils/isFromPageReload';
import { GlobalLocationStateType } from '../../../types/types';

type ResolveState = {
  isLoading: boolean;
};

// Probe master row by id when Detail lands on /detail/:itemId without it in
// `data` (form return, back-button, URL-bar nav). locateAndFetchById loads a
// window of rows so the paginator works. External-url cold-start is handled
// by useExternalUrlDetailGuard.
export const useFormReturnDetailFetchGuard = () => {
  const history = useHistory();
  const routes = useRoutes();
  const { pathname, state } = useLocation<GlobalLocationStateType>();
  const { itemId } = useParams<{ itemId?: string }>();
  const {
    dataset,
    data,
    filter,
    activeFilters,
    locateAndFetchById,
    wasRowJustRemoved,
    dataHookLoadingDatasetId,
    peersetFilterFirstProcessed,
    setPeersetFilterFirstProcessed,
  } = useDatasetContext();
  const { fromItemExternalUrl, getDatasetSessionStorageState } =
    useDatasetSessionStorageContext();

  const dataKey = getDataKey(dataset);
  const lookupAttemptedRef = useRef<boolean>(false);

  const [resolveState, setResolveState] = useState<ResolveState>({
    isLoading: Boolean(
      itemId && !fromItemExternalUrl && !state?.enteredFromExternalUrl
    ),
  });

  // Leave subset* flags intact — SlaveGrid consumes them.
  const cleanupState = useCallback(() => {
    if (
      !state?.fromForms &&
      state?.submitted === undefined &&
      !state?.forceFindInDataset &&
      !state?.forceNewData
    ) {
      return;
    }
    history.replace(pathname, {
      ...state,
      fromForms: undefined,
      submitted: undefined,
      forceFindInDataset: undefined,
      forceNewData: undefined,
    });
  }, [history, pathname, state]);

  useEffect(() => {
    lookupAttemptedRef.current = false;
  }, [pathname, itemId]);

  useEffect(() => {
    if (!itemId || !dataKey || !dataset) {
      setResolveState({ isLoading: false });
      return;
    }
    // External-url display has its own guard — do not run the lookup over it.
    // fromItemExternalUrl flips false mid-display (lastLoadedDataset write +
    // pathname change), so also honor the enteredFromExternalUrl router state
    // stamped by the DatasetContext cold-start redirect.
    if (fromItemExternalUrl || state?.enteredFromExternalUrl) {
      setResolveState({ isLoading: false });
      return;
    }
    // Full reload of a detail URL is redirected to the list by
    // useDatasetUrlDetailReloadGuard — do not start a lookup that would race
    // it (and short-circuit with a rowNotFound toast on filterFirst datasets).
    // Keep the spinner up until the redirect unmounts this detail.
    if (
      isFromPageReload() &&
      getDatasetSessionStorageState().reloadFromItem === true
    )
      return;
    if (dataHookLoadingDatasetId !== undefined) return;

    // filterFirst detail entered without loaded data (e.g. a duplicated tab):
    // wait until the filter definition lands and, when it seeds defaults into
    // activeFilters, until their fetch lands too — the lookup must run against
    // filterFirst-filtered data, not an unfiltered window. When the definition
    // is loaded and activeFilters stayed null (seeding skipped under an
    // external display), nothing more is coming — do not wait forever.
    if (
      dataset?.filterFirst &&
      dataset?.filterId &&
      (filter === null
        ? true
        : activeFilters !== null && data === undefined)
    )
      return;

    // 1) Row already in master data — paginator works straight away.
    const foundInData = (data ?? []).find(
      (row: any) => String(row?.[dataKey]) === itemId
    );
    if (foundInData && !state?.forceNewData) {
      // Deep-link reload on a filterFirst dataset resolved via the seeded
      // defaults — mark filterFirst as passed so leaving the detail does not
      // reopen the filter dialog over already-filtered data.
      if (dataset?.filterFirst && !peersetFilterFirstProcessed) {
        setPeersetFilterFirstProcessed(true);
      }
      setResolveState({ isLoading: false });
      cleanupState();
      return;
    }

    // Row vanished because it was just deleted, not because we navigated to it:
    // Detail is repointing the URL at its successor, probing would toast a lie.
    if (wasRowJustRemoved(itemId)) {
      setResolveState({ isLoading: false });
      return;
    }

    // 2) Single in-flight probe per detail mount.
    if (lookupAttemptedRef.current) return;
    lookupAttemptedRef.current = true;

    // filterFirst peerset flag dies on full reload. If the user is on /detail/
    // they already passed filterFirst once — restore the flag so
    // locateAndFetchById does not short-circuit with "rowNotFound" for
    // filterFirst datasets when state.submitted is false (e.g. back-button or
    // form-cancel).
    const restoredFilterFirstFlag = Boolean(
      dataset?.filterFirst && !peersetFilterFirstProcessed
    );
    if (dataset?.filterFirst) {
      setPeersetFilterFirstProcessed(true);
    }

    setResolveState({ isLoading: true });

    const forceNewData =
      Boolean(state?.submitted) || Boolean(state?.forceNewData);

    locateAndFetchById(
      itemId,
      forceNewData,
      () => {
        setResolveState({ isLoading: false });
        cleanupState();
      },
      () => {
        setResolveState({ isLoading: false });
        cleanupState();
        // Deep-link reload and the row is outside the filterFirst filter:
        // leave the detail for the plain list view (filtered data is already
        // loaded); locateAndFetchById has dispatched the rowNotFound toast.
        if (restoredFilterFirstFlag) {
          history.replace(routes.datasetList(dataset.datasetId));
        }
      }
    );
  }, [
    data,
    state,
    itemId,
    dataKey,
    dataset,
    filter,
    activeFilters,
    dataHookLoadingDatasetId,
    fromItemExternalUrl,
  ]);

  return {
    isLoading: resolveState.isLoading,
  };
};
